import { useEffect, useRef, useState } from 'react';
import type { KeyboardEvent, ReactNode } from 'react';
import { Icon, type IconName } from './Icon';
import { v } from '@/theme/tokens';

export interface DropdownOption {
  value: string;
  label: string;
  hint?: string;
  group?: string;
}

interface DropdownProps {
  value: string | null;
  label: string;
  options: DropdownOption[];
  onSelect: (value: string) => void;
  icon?: IconName;
  footer?: ReactNode;
  disabled?: boolean;
}

/**
 * The header's restaurant and period pickers.
 *
 * Keyboard, as the listbox pattern expects: opening moves focus to the
 * current option, the arrow keys (and Home/End) move between options,
 * Enter or Space chooses, Escape closes and returns focus to the button,
 * Tab closes and moves on. The material (opaque or glass) comes from
 * styles/glass.css (`.rt-trigger`, `.rt-menu`, `.rt-menu__item`).
 */
export function Dropdown({ value, label, options, onSelect, icon, footer, disabled }: DropdownProps) {
  const [open, setOpen] = useState(false);
  const ref = useRef<HTMLDivElement>(null);
  const triggerRef = useRef<HTMLButtonElement>(null);
  const listRef = useRef<HTMLDivElement>(null);

  useEffect(() => {
    if (!open) return;
    const onClick = (e: MouseEvent) => {
      if (ref.current && !ref.current.contains(e.target as Node)) setOpen(false);
    };
    const onKey = (e: globalThis.KeyboardEvent) => {
      if (e.key !== 'Escape') return;
      setOpen(false);
      triggerRef.current?.focus();
    };
    document.addEventListener('mousedown', onClick);
    document.addEventListener('keydown', onKey);
    return () => {
      document.removeEventListener('mousedown', onClick);
      document.removeEventListener('keydown', onKey);
    };
  }, [open]);

  // Focus lands on the chosen option when the list opens.
  useEffect(() => {
    if (!open) return;
    const items = listRef.current?.querySelectorAll<HTMLElement>('[role="option"]');
    if (!items || items.length === 0) return;
    const selected = Array.from(items).find((item) => item.getAttribute('aria-selected') === 'true');
    (selected ?? items[0]).focus();
  }, [open]);

  const onListKeyDown = (event: KeyboardEvent<HTMLDivElement>) => {
    const items = Array.from(
      listRef.current?.querySelectorAll<HTMLElement>('[role="option"]') ?? [],
    );
    if (items.length === 0) return;
    const current = items.indexOf(document.activeElement as HTMLElement);
    let next = -1;
    if (event.key === 'ArrowDown') next = current < 0 ? 0 : Math.min(current + 1, items.length - 1);
    if (event.key === 'ArrowUp') next = current < 0 ? items.length - 1 : Math.max(current - 1, 0);
    if (event.key === 'Home') next = 0;
    if (event.key === 'End') next = items.length - 1;
    if (event.key === 'Tab') {
      setOpen(false);
      return;
    }
    if (next >= 0) {
      event.preventDefault();
      items[next].focus();
    }
  };

  const groups = options.reduce<Record<string, DropdownOption[]>>((acc, option) => {
    const key = option.group ?? '';
    (acc[key] ||= []).push(option);
    return acc;
  }, {});

  return (
    <div ref={ref} style={{ position: 'relative', minWidth: 0 }}>
      <button
        ref={triggerRef}
        type="button"
        className="rt-trigger"
        disabled={disabled}
        onClick={() => setOpen((o) => !o)}
        aria-haspopup="listbox"
        aria-expanded={open}
        style={{
          display: 'flex',
          alignItems: 'center',
          minHeight: 36,
          maxWidth: '100%',
          cursor: disabled ? 'not-allowed' : 'pointer',
          fontWeight: 600,
          fontSize: 13.5,
        }}
      >
        {icon && (
          <span className="rt-trigger__icon" aria-hidden style={{ color: v('ink-muted'), display: 'flex' }}>
            <Icon name={icon} size={15} />
          </span>
        )}
        <span style={{ minWidth: 0, overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap' }}>
          {label}
        </span>
        <span style={{ color: v('ink-muted'), display: 'flex' }}><Icon name="chevron" size={14} /></span>
      </button>

      {open && (
        <div
          ref={listRef}
          role="listbox"
          aria-label={label}
          className="scroll-area rt-menu"
          onKeyDown={onListKeyDown}
          style={{
            position: 'absolute',
            top: 42,
            left: 0,
            minWidth: 230,
            // Never wider than the phone it opens on.
            maxWidth: 'calc(100vw - 24px)',
            maxHeight: 360,
            overflowY: 'auto',
            padding: 6,
            zIndex: 40,
          }}
        >
          {Object.entries(groups).map(([group, items]) => (
            <div key={group || 'default'} role={group ? 'group' : undefined} aria-label={group || undefined}>
              {group && (
                <div
                  style={{
                    fontSize: 11,
                    fontWeight: 700,
                    letterSpacing: 0.4,
                    textTransform: 'uppercase',
                    color: v('ink-faint'),
                    padding: '8px 12px 4px',
                  }}
                >
                  {group}
                </div>
              )}
              {items.map((option) => {
                const active = option.value === value;
                return (
                  <button
                    key={option.value}
                    type="button"
                    role="option"
                    aria-selected={active}
                    className="rt-menu__item"
                    onClick={() => {
                      onSelect(option.value);
                      setOpen(false);
                      triggerRef.current?.focus();
                    }}
                    style={{ minHeight: 36, padding: '8px 12px' }}
                  >
                    {option.label}
                    {option.hint && (
                      <span style={{ display: 'block', fontSize: 12, color: v('ink-muted'), fontWeight: 500 }}>
                        {option.hint}
                      </span>
                    )}
                  </button>
                );
              })}
            </div>
          ))}
          {footer && (
            <div style={{ borderTop: `1px solid ${v('line-soft')}`, marginTop: 4, paddingTop: 4 }}>
              {footer}
            </div>
          )}
        </div>
      )}
    </div>
  );
}
